import axios from 'axios';
import React, { useState, useEffect } from 'react';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import './Join.css';

const STEPS = [
  ['Check your postcode', 'We confirm you\'re inside the boundary automatically — no paperwork.'],
  ['Add your details', 'Just a name and an email. We never share them, ever.'],
  ['Get the updates', 'A friendly email round-up, plus the odd timely heads-up. Unsubscribe anytime.'],
];

const Join = () => {
  const [form, setForm] = useState({ name: '', email: '', houseNameNumber: '', street: '', address: '' });
  const [suggestions, setSuggestions] = useState([]);
  const [isEligible, setIsEligible] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [postcodes, setPostcodes] = useState([]);
  const [loadingPostcodes, setLoadingPostcodes] = useState(true);

  useEffect(() => {
    let mounted = true;
    fetch('/api/approvedPostcodes')
      .then(res => { if (!res.ok) throw new Error(); return res.json(); })
      .then(data => { if (mounted) setPostcodes(Array.isArray(data) ? data : []); })
      .catch(() => {})
      .finally(() => { if (mounted) setLoadingPostcodes(false); });
    return () => { mounted = false; };
  }, []);

  const fetchSuggestions = async (input) => {
    if (input.length < 3) return setSuggestions([]);
    try {
      const res = await axios.get(`https://api.postcodes.io/postcodes?q=${input}`);
      setSuggestions(res.data?.result?.slice(0, 5).map(item => item.postcode) || []);
    } catch { setSuggestions([]); }
  };

  const checkEligibility = (postcode) =>
    postcodes.includes(postcode.toUpperCase().trim().replace(/\s+/g, ' '));

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsEligible(checkEligibility(form.address));
    setSubmitted(true);
  };

  if (loadingPostcodes) return <div className="container" style={{ paddingTop: 48 }}><LoadingSpinner message="Loading eligibility data…" /></div>;

  return (
    <div className="container">
      <section className="page-intro">
        <span className="kicker">Free · takes a minute</span>
        <h1>Join CWARA</h1>
        <p className="lead" style={{ maxWidth: 640 }}>
          Membership is free and open to anyone living within the boundary. Check your
          postcode, add your details, and you'll start getting our updates by email.
        </p>
      </section>

      <section className="section-tight">
        <div className="join-grid">
          {/* ── Form card ──────────────────────────────── */}
          <div className="card join-card">
            <form onSubmit={handleSubmit} noValidate>
              <h3 style={{ marginBottom: 4 }}>1 · Check you're in the area</h3>
              <p className="muted" style={{ fontSize: '0.95rem', marginBottom: 16 }}>
                We'll verify your postcode against the CWARA boundary.
              </p>
              <div className="postcode-row">
                <div style={{ position: 'relative', flex: 1 }}>
                  <input
                    type="text"
                    name="address"
                    required
                    value={form.address}
                    onChange={e => {
                      setForm({ ...form, address: e.target.value });
                      fetchSuggestions(e.target.value);
                    }}
                    autoComplete="off"
                    placeholder="e.g. AL3 5SL"
                  />
                  {suggestions.length > 0 && (
                    <ul className="autocomplete-list">
                      {suggestions.map((s, i) => (
                        <li key={i} onClick={() => { setForm({ ...form, address: s }); setSuggestions([]); }}>{s}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              {submitted && isEligible && (
                <div className="checkresult ok">✓ You're in the CWARA area — welcome!</div>
              )}
              {submitted && !isEligible && (
                <div className="checkresult no">That postcode looks outside our boundary. Try another, or <a href="/contact">contact us</a>.</div>
              )}

              <hr className="rule" style={{ margin: '24px 0' }} />

              <h3 style={{ marginBottom: 14 }}>2 · Your details</h3>
              <label>
                <span>Full name</span>
                <input type="text" required value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  placeholder="Jane Smith" />
              </label>
              <label>
                <span>Email address</span>
                <input type="email" required value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                  placeholder="jane@example.com" />
              </label>
              <label>
                <span>House name or number <small>(optional)</small></span>
                <input type="text" value={form.houseNameNumber}
                  onChange={e => setForm({ ...form, houseNameNumber: e.target.value })}
                  placeholder="e.g. 3 or Waverley House" />
              </label>
              <label>
                <span>Street name <small>(optional)</small></span>
                <input type="text" value={form.street}
                  onChange={e => setForm({ ...form, street: e.target.value })}
                  placeholder="e.g. Waverley Road" />
              </label>

              <p className="join-disclaimer muted">
                By submitting this form you consent to CWARA storing your details for
                community updates. Your data will not be shared and will be handled in
                accordance with our <a href="/privacy">privacy policy</a>.
              </p>

              <button type="submit" className="btn btn-primary btn-lg"
                style={{ width: '100%', justifyContent: 'center', marginTop: 4 }}>
                Join CWARA
              </button>
            </form>

            {submitted && isEligible && (
              <div className="donate-box">
                <h3>Support CWARA (Optional)</h3>
                <p>Help us continue our work by making a voluntary donation via bank transfer:</p>
                <p style={{ marginTop: 8 }}>
                  <strong>Account Name:</strong> CWARA &nbsp;·&nbsp;
                  <strong>Sort Code:</strong> 08-92-99 &nbsp;·&nbsp;
                  <strong>Account No:</strong> 6580 0339
                </p>
              </div>
            )}
          </div>

          {/* ── How it works ───────────────────────────── */}
          <div className="join-aside">
            <div className="sec-head">
              <span className="kicker">How it works</span>
              <h2>Three small steps</h2>
            </div>
            <div className="steps-list">
              {STEPS.map(([title, body], i) => (
                <div className="step-item" key={i}>
                  <div className="step-num">{i + 1}</div>
                  <div>
                    <h3>{title}</h3>
                    <p className="muted">{body}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="card privacy-card">
              <span className="badge olive"><span className="badge-dot"></span>Privacy</span>
              <p style={{ marginTop: 10, fontSize: '0.95rem', color: '#41471c' }}>
                Your details stay with the committee and are only used to send you CWARA
                updates. That's the whole deal.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Join;
